import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import isEqual from 'lodash/isEqual';
import Card from '@components-clean/core/card';
import EditCard from '@components-clean/core/edit-card';
import InformationIcon from '@components-clean/core/icons/information-icon';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { MultiSelect } from '@components-clean/core/multi-select';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowError } from '@utils/notification';
import { GET_STUDY_AUTOMATION_CONFIG, SAVE_STUDY_AUTOMATION_CONFIG } from '@queries/central-monitoring';
import {
    getStudyAutomationConfig,
    getStudyAutomationConfigVariables,
    addStudyAutomationConfig,
    addStudyAutomationConfigVariables,
} from '@queries/generated/central-monitoring.generated';

export const COMPONENT_NAME = 'dataset-hub-automation-options-card';

interface IProps {
    studyRef: string;
}

const AutomationOptionsCard = ({ studyRef }: IProps) => {
    const user = useAuthorization();
    if (!user) throw new Error('User not found');

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub.generalAutomationOptions',
    });

    interface IFormData {
        excludedStatuses: string[];
    }

    const EMPTY_FORM: IFormData = {
        excludedStatuses: [],
    };

    const dataToFormData = (data: getStudyAutomationConfig) => {
        return {
            excludedStatuses: data.getStudyAutomationConfig.excludedSiteStatuses,
        };
    };

    const {
        data,
        loading: isFetching,
        error: loadError,
        refetch,
    } = useQuery<getStudyAutomationConfig, getStudyAutomationConfigVariables>(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: studyRef },
    });

    const [save, { loading: isSaving }] = useMutation<addStudyAutomationConfig, addStudyAutomationConfigVariables>(
        SAVE_STUDY_AUTOMATION_CONFIG,
    );

    const isLoading = isFetching || isSaving;

    const [formData, setFormData] = useState<IFormData>(EMPTY_FORM);
    const [isEditing, setIsEditing] = useState(false);

    const resetForm = () => {
        // If there is no data, reset the form to the empty state.
        if (!data) {
            setFormData(EMPTY_FORM);
            return;
        }

        setFormData(dataToFormData(data));
    };

    // Reset the form when the current dataset changes.
    useEffect(() => {
        resetForm();
        setIsEditing(false);
    }, [data]);

    // Reset the form when the form is no longer being edited
    useEffect(() => {
        if (isEditing) return;
        resetForm();
    }, [isEditing]);

    useEffect(() => {
        if (!data) return;

        const originalFormData = dataToFormData(data);

        const isDirty = !isEqual(originalFormData, formData);
        if (isDirty !== componentHasChanges(COMPONENT_NAME)) setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [formData]);

    const onSaveHandler = async () => {
        try {
            const body = {
                variables: {
                    studyId: studyRef,
                    excludedSiteStatuses: formData.excludedStatuses,
                },
            };

            await save(body);
            await refetch();

            setIsEditing(false);
            setComponentHasChanges(COMPONENT_NAME, false);
        } catch {
            ShowError(t('errors.save'));
        }
    };

    const content = (
        <>
            {loadError ? (
                <InfoField type="Error" message={t('errors.load')} layout="center" />
            ) : (
                <div className="form">
                    <MultiSelect
                        label={
                            <>
                                Site Statuses
                                <InformationIcon
                                    title={t('tooltips.statusesToExclude')}
                                    className="k-ml-1.5"
                                    dataTestId="tooltip"
                                />
                            </>
                        }
                        data-testid="site-statuses-to-exclude-multi-select"
                        inline
                        data={[]}
                        value={formData.excludedStatuses}
                        onChange={(e) => {
                            const value = e.value as string[];
                            setFormData((prev) => ({ ...prev, excludedStatuses: value }));
                        }}
                        isEditing={isEditing}
                    />
                </div>
            )}
            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                <Loading />
            </div>
        </>
    );

    const hasWritePermission = user.canWrite?.({
        module: Constants.Modules.CM2,
        element: Constants.DataElements.CentralMonitoringDefault,
        permission: Permissions.ReadWrite,
    });

    const props = {
        title: t('title'),
        'data-testid': 'automation-options-card',
        className: 'automation-options-card',
    };

    if (hasWritePermission && !IsPopOut()) {
        return (
            <EditCard
                {...props}
                isEditMode={isEditing}
                onEdit={() => setIsEditing(true)}
                onSave={onSaveHandler}
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) return;
                    }
                    setIsEditing(false);
                }}
            >
                {content}
            </EditCard>
        );
    }

    return <Card {...props}>{content}</Card>;
};

export default AutomationOptionsCard;
